import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import type { ShiftStreamEvent } from 'shared-models';
import { shiftEmployeesService } from '../../services/employees/shift-employees.service';
import { shiftScheduleService } from '../../services/schedule/shift-schedule.service';
import { useShiftAuth } from '../../contexts/ShiftAuthProvider';
import { useShiftEvents } from '../../contexts/ShiftEventsProvider';
import { applyCandidateSchedulingEvent } from './work-schedule-page.utils';
import type { CandidateSchedulingEvent } from './work-schedule-page.utils';
import { resolveShiftCapabilities } from '../../utils/auth/shift-permissions';
import type {
  ShiftScheduleRole,
  ShiftScheduleSlotCandidate,
  ShiftScheduleSection,
  ShiftScheduleShift,
  ShiftScheduleSlot,
  ShiftScheduleWeek,
  ShiftScheduleEmployeeInsight,
} from '../../models/schedule.types';
import type { ShiftEmployeeDirectoryManagerRow } from '../../models/employees.types';
import { type NameModalState, type SlotModalState } from './work-schedule-page.types';
import { getCurrentWeekStartIsoDate, formatWeekRange, resolveWeekStartFromIsoDate } from '../../utils/date.utils';
import {
  TIME_PATTERN,
  normalizeTimeForInput,
  describeCandidateConflict,
  toWeekDays,
  translateScheduleErrorMessage,
  showActionSuccess,
  showActionError,
  sortSections,
} from './work-schedule-page.utils';

export function useWorkSchedulePageController() {
  const { user } = useShiftAuth();

  const { canAssignEmployees, canManageSchedule } = resolveShiftCapabilities(user);

  const [tipDate, setTipDate] = useState<string | null>(null);
  const [weekStartDate, setWeekStartDate] = useState<string>(() => getCurrentWeekStartIsoDate());
  const [scheduleWeek, setScheduleWeek] = useState<ShiftScheduleWeek | null>(null);
  const [isLoadingWeek, setIsLoadingWeek] = useState(true);
  const [weekError, setWeekError] = useState<string | null>(null);
  const [selectedDayOffsets, setSelectedDayOffsets] = useState<Set<number>>(new Set());
  const [selectedShiftNames, setSelectedShiftNames] = useState<Set<string>>(new Set());
  const [selectedRoleNames, setSelectedRoleNames] = useState<Set<string>>(new Set());
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [busyKeys, setBusyKeys] = useState<Set<string>>(new Set());
  const [nameModal, setNameModal] = useState<NameModalState | null>(null);
  const [slotModal, setSlotModal] = useState<SlotModalState | null>(null);
  const [slotCandidates, setSlotCandidates] = useState<ShiftScheduleSlotCandidate[]>([]);
  const [slotCandidatesSearch, setSlotCandidatesSearch] = useState('');
  const [isLoadingSlotCandidates, setIsLoadingSlotCandidates] = useState(false);
  const [isSavingModal, setIsSavingModal] = useState(false);
  const [createSlotEmployees, setCreateSlotEmployees] = useState<ShiftEmployeeDirectoryManagerRow[]>([]);
  const [employeeInsight, setEmployeeInsight] = useState<ShiftScheduleEmployeeInsight | null>(null);
  const [isLoadingEmployeeInsight, setIsLoadingEmployeeInsight] = useState(false);

  const weekLoadRequestIdRef = useRef(0);

  const employeeInsightRequestIdRef = useRef(0);

  const slotCandidatesRequestIdRef = useRef(0);
  const candidateOverlays = useRef(new Map<string, CandidateSchedulingEvent>());
  const latestEmployeeLimits = useRef(new Map<number,import('shared-models').ShiftEmployeeLimits>());
  const keepLatestWeeklyLimits = (status:import('shared-models').ShiftEmployeeWeeklyLimits) => {
    if ((latestEmployeeLimits.current.get(status.limits.employeePk)?.revision ?? -1) < status.limits.revision)
      latestEmployeeLimits.current.set(status.limits.employeePk,status.limits);
    return {...status,limits:latestEmployeeLimits.current.get(status.limits.employeePk)!};
  };
  const sectionTipOverlays = useRef(new Map<number, { minimumTip: number; revision: number }>());
  const nameSaveLock = useRef(false);
  const slotSaveLock = useRef(false);
  const publicationLock = useRef(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const publicationEvents = useRef(new Map<string, Extract<ShiftStreamEvent, { type: 'schedule.publicationChanged' }>>());
  const draftEvents = useRef(new Map<string, Extract<ShiftStreamEvent, { type: 'schedule.draftChanged' }>['snapshot']>());
  const applyPublication = (week: ShiftScheduleWeek): ShiftScheduleWeek => {
    const draft = draftEvents.current.get(week.weekStartDate);
    if (user?.role === 'manager' && draft && draft.revision > (week.staffingRevision ?? 0)) {
      week = { ...week, sections: draft.sections, employeeInsights: draft.employeeInsights,
        publication: draft.publication, staffingRevision: draft.revision };
    }
    const withLimits = week.employeeInsights ? {...week,employeeInsights:week.employeeInsights.map(summary => summary.weeklyLimits
      ? {...summary,weeklyLimits:keepLatestWeeklyLimits(summary.weeklyLimits)} : summary)} : week;
    const event = publicationEvents.current.get(week.weekStartDate);
    if (!event || event.publication.revision < (week.publication?.revision ?? 0)) return withLimits;
    return { ...withLimits, publication: { ...week.publication, ...event.publication },
      ...(user?.role === 'manager' ? {} : { sections: event.sections }) };
  };
  useShiftEvents(event => {
    if (event.type === 'schedule.draftChanged') {
      if (user?.role !== 'manager') return;
      const draft = event.snapshot;
      if ((draftEvents.current.get(draft.weekStartDate)?.revision ?? 0) >= draft.revision) return;
      draftEvents.current.set(draft.weekStartDate, draft);
      setScheduleWeek(current => current ? applyPublication(current) : current);
      return;
    }
    if (event.type === 'sse.resync') {
      void loadWeek(weekStartDate);
      return;
    }
    if (event.type === 'schedule.publicationChanged') {
      if ((publicationEvents.current.get(event.weekStartDate)?.publication.revision ?? 0) >= event.publication.revision) return;
      publicationEvents.current.set(event.weekStartDate, event);
      setScheduleWeek(current => current ? applyPublication(current) : current);
      return;
    }
    if (event.type === 'sectionTipMinimum.upserted') {
      if (user?.role !== 'manager') return;
      if ((sectionTipOverlays.current.get(event.sectionId)?.revision ?? 0) >= event.revision) return;
      sectionTipOverlays.current.set(event.sectionId, event);
      setScheduleWeek(current => current ? { ...current, sections: current.sections.map(section => section.id === event.sectionId
        && (section.tipRevision ?? 0) < event.revision ? { ...section, minimumTip: event.minimumTip, tipRevision: event.revision } : section) } : current);
      setNameModal(current => current?.kind === 'edit-section' && current.sectionId === event.sectionId && current.tipRevision !== event.revision
        ? { ...current, tipConflict: true } : current);
      return;
    }
    if (event.type === 'employeeLimits.upserted') {
      if (user?.role !== 'manager' || (latestEmployeeLimits.current.get(event.setting.employeePk)?.revision ?? -1) >= event.setting.revision) return;
      latestEmployeeLimits.current.set(event.setting.employeePk,event.setting);
      setScheduleWeek(current => current ? applyPublication(current) : current);
    }
    if (event.type !== 'employeePriority.upserted' && event.type !== 'employeeEligibility.upserted' && event.type !== 'employeeLimits.upserted') return;
    if (!slotModal || slotModal.mode !== 'edit') return;
    if (event.type === 'employeeEligibility.upserted' && event.setting.roleId !== slotModal.roleId) return;
    const key = `${event.type}:${event.setting.employeePk}`;
    if ((candidateOverlays.current.get(key)?.setting.revision ?? 0) >= event.setting.revision) return;
    candidateOverlays.current.set(key, event);
    setSlotCandidates(current => applyCandidateSchedulingEvent(current, event, slotModal.roleId));
  });

  const duplicateRoleInFlightRef = useRef<Set<number>>(new Set());

  const filtersWrapRef = useRef<HTMLDivElement>(null);

  const isBusy = (key: string): boolean => busyKeys.has(key);

  const setBusy = (key: string, value: boolean) => {
    setBusyKeys((current) => {
      const next = new Set(current);
      if (value) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const loadWeek = async (targetWeekStart: string) => {
    const requestId = weekLoadRequestIdRef.current + 1;
    weekLoadRequestIdRef.current = requestId;
    setIsLoadingWeek(true);
    setWeekError(null);

    try {
      const payload = await shiftScheduleService.getWeek(targetWeekStart);
      if (requestId !== weekLoadRequestIdRef.current) {
        return;
      }

      setScheduleWeek(applyPublication({ ...payload, sections: payload.sections.map(section => {
        const latest = sectionTipOverlays.current.get(section.id);
        return user?.role === 'manager' && latest && latest.revision > (section.tipRevision ?? 0) ? { ...section, minimumTip: latest.minimumTip, tipRevision: latest.revision } : section;
      }) }));
      setSlotCandidates(current => current.map(candidate => candidate.weeklyLimits
        ? {...candidate,weeklyLimits:keepLatestWeeklyLimits(candidate.weeklyLimits)} : candidate));
    } catch (requestError: unknown) {
      if (requestId !== weekLoadRequestIdRef.current) {
        return;
      }

      setWeekError(translateScheduleErrorMessage(requestError, 'לא ניתן לטעון את סידור העבודה.'));
    } finally {
      if (requestId === weekLoadRequestIdRef.current) {
        setIsLoadingWeek(false);
      }
    }
  };

  useEffect(() => {
    employeeInsightRequestIdRef.current += 1;
    setEmployeeInsight(null);
    setIsLoadingEmployeeInsight(false);
    void loadWeek(weekStartDate);
  }, [weekStartDate]);

  const onOpenEmployeeInsight = async (employeePk: number) => {
    if (user?.role !== 'manager') return;
    const requestId = employeeInsightRequestIdRef.current + 1;
    employeeInsightRequestIdRef.current = requestId;
    setIsLoadingEmployeeInsight(true);
    try {
      const insight = await shiftScheduleService.getEmployeeInsight(employeePk, weekStartDate);
      if (requestId === employeeInsightRequestIdRef.current) {
        setEmployeeInsight(insight);
      }
    } catch (requestError) {
      if (requestId === employeeInsightRequestIdRef.current) {
        showActionError(translateScheduleErrorMessage(requestError, 'לא ניתן לטעון את פרטי העובד.'));
      }
    } finally {
      if (requestId === employeeInsightRequestIdRef.current) {
        setIsLoadingEmployeeInsight(false);
      }
    }
  };

  const onCloseEmployeeInsight = () => {
    employeeInsightRequestIdRef.current += 1;
    setEmployeeInsight(null);
    setIsLoadingEmployeeInsight(false);
  };

  useEffect(() => {
    if (!isFiltersOpen) {
      return;
    }

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!filtersWrapRef.current?.contains(event.target as Node)) {
        setIsFiltersOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [isFiltersOpen]);

  const sortedSections = useMemo(() => {
    if (!scheduleWeek) {
      return [] as ShiftScheduleSection[];
    }

    return sortSections(scheduleWeek.sections ?? []);
  }, [scheduleWeek]);

  const weekDays = useMemo(() => {
    const base = scheduleWeek?.weekStartDate ?? weekStartDate;
    return toWeekDays(base);
  }, [scheduleWeek?.weekStartDate, weekStartDate]);

  const employeeInsightSummaries = useMemo(
    () => new Map((scheduleWeek?.employeeInsights ?? []).map((summary) => [summary.employeePk, summary])),
    [scheduleWeek?.employeeInsights],
  );

  const shiftFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(sortedSections.flatMap((section) => section.shifts.map((shift) => shift.shiftName))),
      ),
    [sortedSections],
  );

  const roleFilterOptions = useMemo(
    () =>
      Array.from(
        new Set(
          sortedSections.flatMap((section) =>
            section.shifts.flatMap((shift) => shift.roles.map((role) => role.roleName)),
          ),
        ),
      ),
    [sortedSections],
  );

  const visibleWeekDays = useMemo(() => {
    if (selectedDayOffsets.size === 0) {
      return weekDays;
    }

    return weekDays.filter((_day, offset) => selectedDayOffsets.has(offset));
  }, [selectedDayOffsets, weekDays]);

  const activeFilterCount = selectedDayOffsets.size + selectedShiftNames.size + selectedRoleNames.size;

  const hasActiveFilters = activeFilterCount > 0;

  const visibleSections = useMemo(() => {
    if (!hasActiveFilters) {
      return sortedSections;
    }

    const visibleDates = new Set(visibleWeekDays.map((day) => day.dateDay));
    return sortedSections
      .map((section) => ({
        ...section,
        shifts: section.shifts
          .filter((shift) => selectedShiftNames.size === 0 || selectedShiftNames.has(shift.shiftName))
          .map((shift) => ({
            ...shift,
            roles: shift.roles.filter(
              (role) =>
                (selectedRoleNames.size === 0 || selectedRoleNames.has(role.roleName)) &&
                role.slots.some((slot) => visibleDates.has(slot.dateDay)),
            ),
          }))
          .filter((shift) => shift.roles.length > 0),
      }))
      .filter((section) => section.shifts.length > 0);
  }, [hasActiveFilters, selectedRoleNames, selectedShiftNames, sortedSections, visibleWeekDays]);

  const toggleSetValue = <T>(setter: React.Dispatch<React.SetStateAction<Set<T>>>, value: T) => {
    setter((current) => {
      const next = new Set(current);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const onResetFilters = () => {
    setSelectedDayOffsets(new Set());
    setSelectedShiftNames(new Set());
    setSelectedRoleNames(new Set());
  };

  const onSelectAllFilters = () => {
    setSelectedDayOffsets(new Set(weekDays.map((_day, offset) => offset)));
    setSelectedShiftNames(new Set(shiftFilterOptions));
    setSelectedRoleNames(new Set(roleFilterOptions));
  };

  const filteredSlotCandidates = useMemo(() => {
    if (slotCandidatesSearch.trim().length === 0) {
      return slotCandidates;
    }

    const searchValue = slotCandidatesSearch.trim().toLowerCase();
    return slotCandidates.filter((candidate) => {
      const fullName = `${candidate.firstName} ${candidate.lastName}`.toLowerCase();
      return fullName.includes(searchValue);
    });
  }, [slotCandidates, slotCandidatesSearch]);

  const refreshCurrentWeek = async () => {
    await loadWeek(weekStartDate);
  };

  const onChangePublication = async (isPublished: boolean) => {
    const current = scheduleWeek;
    if (publicationLock.current || isLoadingWeek || !canManageSchedule || !current?.publication
      || (isPublished && !current.publication.draftHash)) return;
    publicationLock.current = true;
    setIsPublishing(true);
    try {
      await shiftScheduleService.patchPublication(current.weekStartDate, {
        isPublished, expectedRevision: current.publication.revision,
        ...(isPublished ? { expectedDraftHash: current.publication.draftHash } : {}),
      });
      showActionSuccess(isPublished ? 'הסידור פורסם.' : 'פרסום הסידור בוטל.');
    } catch (error) {
      showActionError(translateScheduleErrorMessage(error, 'לא ניתן לשנות את הפרסום. רעננו את הסידור ונסו שוב.'));
    } finally {
      publicationLock.current = false;
      setIsPublishing(false);
    }
  };

  const onCloseSlotModal = () => {
    setSlotModal(null);
    setSlotCandidates([]);
    setSlotCandidatesSearch('');
    setIsLoadingSlotCandidates(false);
  };

  const loadSlotCandidates = async (slotId: number) => {
    candidateOverlays.current.clear();
    const requestId = slotCandidatesRequestIdRef.current + 1;
    slotCandidatesRequestIdRef.current = requestId;
    setIsLoadingSlotCandidates(true);

    try {
      const payload = await shiftScheduleService.getSlotCandidates(slotId);
      if (requestId !== slotCandidatesRequestIdRef.current) {
        return;
      }

      let candidates = payload.candidates;
      for (const event of candidateOverlays.current.values()) candidates = applyCandidateSchedulingEvent(candidates, event, payload.slot.roleId);
      candidates = candidates.map(candidate => candidate.weeklyLimits ? {...candidate,weeklyLimits:keepLatestWeeklyLimits(candidate.weeklyLimits)} : candidate);
      setScheduleWeek(current => current ? applyPublication(current) : current);
      setSlotCandidates(candidates);
      setSlotModal((current) => {
        if (!current || current.mode !== 'edit' || current.slotId !== slotId) {
          return current;
        }

        return {
          ...current,
          employeePk: payload.slot.employeePk,
          initialEmployeePk: payload.slot.employeePk,
          currentEmployeeName: payload.slot.employeeName,
        };
      });
    } catch (requestError: unknown) {
      if (requestId !== slotCandidatesRequestIdRef.current) {
        return;
      }

      setSlotCandidates([]);
      showActionError(translateScheduleErrorMessage(requestError, 'טעינת רשימת העובדים נכשלה.'));
    } finally {
      if (requestId === slotCandidatesRequestIdRef.current) {
        setIsLoadingSlotCandidates(false);
      }
    }
  };

  useEffect(() => {
    if (!canAssignEmployees || !slotModal || slotModal.mode !== 'edit' || !slotModal.slotId) {
      return;
    }

    setSlotCandidatesSearch('');
    void loadSlotCandidates(slotModal.slotId);
  }, [canAssignEmployees, slotModal?.mode, slotModal?.slotId]);

  const onDeleteSection = async (sectionId: number) => {
    if (!window.confirm('למחוק את המחלקה?')) {
      return;
    }

    const key = `delete-section-${sectionId}`;
    setBusy(key, true);

    try {
      await shiftScheduleService.deleteSection(sectionId);
      showActionSuccess('המחלקה נמחקה בהצלחה.');
      await refreshCurrentWeek();
    } catch (requestError: unknown) {
      showActionError(translateScheduleErrorMessage(requestError, 'מחיקת המחלקה נכשלה.'));
    } finally {
      setBusy(key, false);
    }
  };

  const onDeleteShift = async (shiftId: number) => {
    if (!window.confirm('למחוק את המשמרת?')) {
      return;
    }

    const key = `delete-shift-${shiftId}`;
    setBusy(key, true);

    try {
      await shiftScheduleService.deleteShift(shiftId);
      showActionSuccess('המשמרת נמחקה בהצלחה.');
      await refreshCurrentWeek();
    } catch (requestError: unknown) {
      showActionError(translateScheduleErrorMessage(requestError, 'מחיקת המשמרת נכשלה.'));
    } finally {
      setBusy(key, false);
    }
  };

  const onDeleteRole = async (roleId: number) => {
    if (!window.confirm('למחוק את התפקיד?')) {
      return;
    }

    const key = `delete-role-${roleId}`;
    setBusy(key, true);

    try {
      await shiftScheduleService.deleteRole(roleId);
      showActionSuccess('התפקיד נמחק בהצלחה.');
      await refreshCurrentWeek();
    } catch (requestError: unknown) {
      showActionError(translateScheduleErrorMessage(requestError, 'מחיקת התפקיד נכשלה.'));
    } finally {
      setBusy(key, false);
    }
  };

  const onDuplicateRole = async (shiftId: number, role: ShiftScheduleRole) => {
    if (!canManageSchedule || duplicateRoleInFlightRef.current.has(role.id)) {
      return;
    }

    duplicateRoleInFlightRef.current.add(role.id);
    const key = `duplicate-role-${role.id}`;
    setBusy(key, true);
    try {
      await shiftScheduleService.createRole({ sectionShiftsPk: shiftId, roleName: role.roleName });
      showActionSuccess('התפקיד שוכפל בהצלחה');
      await refreshCurrentWeek();
    } catch (requestError: unknown) {
      showActionError(translateScheduleErrorMessage(requestError, 'שכפול התפקיד נכשל.'));
    } finally {
      duplicateRoleInFlightRef.current.delete(role.id);
      setBusy(key, false);
    }
  };

  const onDeleteSlot = async (slotId: number): Promise<boolean> => {
    if (!window.confirm('למחוק את הסלוט?')) {
      return false;
    }

    const key = `delete-slot-${slotId}`;
    setBusy(key, true);

    try {
      await shiftScheduleService.deleteSlot(slotId);
      showActionSuccess('הסלוט נמחק בהצלחה.');
      await refreshCurrentWeek();
      return true;
    } catch (requestError: unknown) {
      showActionError(translateScheduleErrorMessage(requestError, 'מחיקת הסלוט נכשלה.'));
      return false;
    } finally {
      setBusy(key, false);
    }
  };

  const onOpenCreateSectionModal = () => {
    setNameModal({
      kind: 'create-section',
      title: 'הוספת מחלקה',
      submitLabel: 'צור מחלקה',
      value: '',
      minimumTip: '',
    });
  };

  const onOpenEditSectionModal = (section: ShiftScheduleSection) => {
    setNameModal({
      kind: 'edit-section',
      title: 'עריכת מחלקה',
      submitLabel: 'שמור',
      value: section.sectionName,
      initialValue: section.sectionName,
      minimumTip: String(section.minimumTip ?? 0),
      initialMinimumTip: section.minimumTip ?? 0,
      tipRevision: section.tipRevision ?? 1,
      sectionId: section.id,
    });
  };

  const onOpenCreateShiftModal = (sectionId: number) => {
    setNameModal({
      kind: 'create-shift',
      title: 'הוספת משמרת',
      submitLabel: 'צור משמרת',
      value: '',
      sectionId,
    });
  };

  const onOpenEditShiftModal = (shift: ShiftScheduleShift) => {
    setNameModal({
      kind: 'edit-shift',
      title: 'עריכת משמרת',
      submitLabel: 'שמור',
      value: shift.shiftName,
      shiftId: shift.id,
    });
  };

  const onOpenCreateRoleModal = (shiftId: number) => {
    setNameModal({
      kind: 'create-role',
      title: 'הוספת תפקיד',
      submitLabel: 'צור תפקיד',
      value: '',
      shiftId,
    });
  };

  const onOpenEditRoleModal = (role: ShiftScheduleRole) => {
    setNameModal({
      kind: 'edit-role',
      title: 'עריכת תפקיד',
      submitLabel: 'שמור',
      value: role.roleName,
      roleId: role.id,
    });
  };

  const onOpenCreateSlotModal = (roleId: number, dateDay: string) => {
    setSlotCandidates([]);
    setSlotCandidatesSearch('');
    setSlotModal({
      mode: 'create',
      roleId,
      dateDay,
      startHour: '09:00',
      endHour: '17:00',
      employeePk: null,
      initialEmployeePk: null,
      currentEmployeeName: null,
    });
    setCreateSlotEmployees([]);
    if (canAssignEmployees) {
      void shiftEmployeesService
        .listDirectory()
        .then((employees) => {
          setCreateSlotEmployees(
            employees.filter(
              (employee): employee is ShiftEmployeeDirectoryManagerRow => 'employeePk' in employee,
            ),
          );
        })
        .catch((requestError: unknown) => {
          showActionError(translateScheduleErrorMessage(requestError, 'טעינת רשימת העובדים נכשלה.'));
        });
    }
  };

  const onOpenEditSlotModal = (slot: ShiftScheduleSlot) => {
    setSlotCandidates([]);
    setSlotCandidatesSearch('');
    setSlotModal({
      mode: 'edit',
      roleId: slot.sectionRolePk,
      slotId: slot.id,
      dateDay: slot.dateDay,
      startHour: normalizeTimeForInput(slot.startHour),
      endHour: normalizeTimeForInput(slot.endHour),
      initialStartHour: normalizeTimeForInput(slot.startHour),
      initialEndHour: normalizeTimeForInput(slot.endHour),
      employeePk: slot.employeePk,
      initialEmployeePk: slot.employeePk,
      currentEmployeeName: slot.employeeName,
    });
  };

  const onNameModalSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!nameModal || nameSaveLock.current || nameModal.tipConflict) {
      return;
    }

    const trimmed = nameModal.value.trim();
    if (trimmed.length === 0) {
      showActionError('יש להזין שם.');
      return;
    }

    if (nameModal.minimumTip !== undefined && (!/^\d*$/.test(nameModal.minimumTip) || Number(nameModal.minimumTip) > 2147483647)) {
      showActionError('מינימום הטיפ חייב להיות סכום שלם ולא שלילי.');
      return;
    }
    nameSaveLock.current = true;
    setIsSavingModal(true);

    try {
      if (nameModal.kind === 'create-section') {
        await shiftScheduleService.createSection({ sectionName: trimmed, minimumTip: Number(nameModal.minimumTip ?? 0) });
        showActionSuccess('המחלקה נוספה בהצלחה.');
      } else if (nameModal.kind === 'edit-section' && nameModal.sectionId) {
        const patch: import('shared-models').ShiftDepartmentTipPatch = {};
        if (trimmed !== nameModal.initialValue) patch.sectionName = trimmed;
        if (Number(nameModal.minimumTip ?? 0) !== nameModal.initialMinimumTip) {
          patch.minimumTip = Number(nameModal.minimumTip ?? 0);
          patch.expectedTipRevision = nameModal.tipRevision;
        }
        if (!Object.keys(patch).length) { setNameModal(null); return; }
        await shiftScheduleService.updateSection(nameModal.sectionId, patch);
        showActionSuccess('שם המחלקה עודכן.');
      } else if (nameModal.kind === 'create-shift' && nameModal.sectionId) {
        await shiftScheduleService.createShift({ sectionPk: nameModal.sectionId, shiftName: trimmed });
        showActionSuccess('המשמרת נוספה בהצלחה.');
      } else if (nameModal.kind === 'edit-shift' && nameModal.shiftId) {
        await shiftScheduleService.updateShift(nameModal.shiftId, { shiftName: trimmed });
        showActionSuccess('שם המשמרת עודכן.');
      } else if (nameModal.kind === 'create-role' && nameModal.shiftId) {
        await shiftScheduleService.createRole({ sectionShiftsPk: nameModal.shiftId, roleName: trimmed });
        showActionSuccess('התפקיד נוסף בהצלחה.');
      } else if (nameModal.kind === 'edit-role' && nameModal.roleId) {
        await shiftScheduleService.updateRole(nameModal.roleId, { roleName: trimmed });
        showActionSuccess('שם התפקיד עודכן.');
      }

      setNameModal(null);
      await refreshCurrentWeek();
    } catch (requestError: unknown) {
      showActionError(translateScheduleErrorMessage(requestError, 'שמירת השינויים נכשלה.'));
    } finally {
      nameSaveLock.current = false;
      setIsSavingModal(false);
    }
  };

  const onSlotModalSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!slotModal || slotSaveLock.current) {
      return;
    }

    if (slotModal.mode === 'edit') {
      const draft = draftEvents.current.get(resolveWeekStartFromIsoDate(slotModal.dateDay));
      const canonical = draft?.sections.flatMap(section => section.shifts.flatMap(shift => shift.roles.flatMap(role => role.slots)))
        .find(slot => slot.id === slotModal.slotId);
      if (draft && (!canonical || canonical.employeePk !== slotModal.initialEmployeePk
        || canonical.startHour !== slotModal.initialStartHour || canonical.endHour !== slotModal.initialEndHour)) {
        showActionError('המשבצת השתנתה. יש לסגור ולפתוח אותה מחדש לפני שמירה.');
        return;
      }
    }

    const startHour = normalizeTimeForInput(slotModal.startHour);
    const endHour = normalizeTimeForInput(slotModal.endHour);
    if (!TIME_PATTERN.test(startHour) || !TIME_PATTERN.test(endHour)) {
      showActionError('יש להזין שעה בפורמט HH:mm, לדוגמה 09:15 או 23:45.');
      return;
    }
    if (startHour === endHour) {
      showActionError('שעת ההתחלה ושעת הסיום אינן יכולות להיות זהות.');
      return;
    }

    const timePatch = {
      ...(startHour !== slotModal.initialStartHour ? { startHour } : {}),
      ...(endHour !== slotModal.initialEndHour ? { endHour } : {}),
    };
    const hoursChanged = canManageSchedule && Object.keys(timePatch).length > 0;
    const assignmentChanged = slotModal.employeePk !== slotModal.initialEmployeePk;
    if (slotModal.mode === 'edit' && !hoursChanged && !(canAssignEmployees && assignmentChanged)) {
      onCloseSlotModal();
      return;
    }

    slotSaveLock.current = true;
    setIsSavingModal(true);

    try {
      if (slotModal.mode === 'create') {
        await shiftScheduleService.createSlot({
          sectionRolePk: slotModal.roleId,
          dateDay: slotModal.dateDay,
          startHour,
          endHour,
          employeePk: slotModal.employeePk,
        });
        showActionSuccess('הסלוט נוצר בהצלחה.');
      } else if (slotModal.slotId) {
        const selectedCandidate =
          slotCandidates.find((candidate) => candidate.employeePk === slotModal.employeePk) ?? null;

        if (assignmentChanged && selectedCandidate?.hasOtherSlotSameDay) {
          const conflictHint = describeCandidateConflict(selectedCandidate);
          const warning = conflictHint
            ? `העובד כבר משובץ במשמרת אחרת ביום זה. האם לשבץ בכל זאת?\n${conflictHint}`
            : 'העובד כבר משובץ במשמרת אחרת ביום זה. האם לשבץ בכל זאת?';

          if (!window.confirm(warning)) {
            return;
          }
        }

        if (hoursChanged) {
          await shiftScheduleService.updateSlot(slotModal.slotId, timePatch);
        }

        if (canAssignEmployees && assignmentChanged) {
          await shiftScheduleService.assignSlotEmployee(slotModal.slotId, {
            employeePk: slotModal.employeePk,
          });
          if (slotModal.employeePk === null) {
            showActionSuccess('השיבוץ הוסר והסלוט עודכן.');
          } else if (slotModal.initialEmployeePk === null) {
            showActionSuccess('העובד שובץ בהצלחה והסלוט עודכן.');
          } else {
            showActionSuccess('השיבוץ הוחלף בהצלחה והסלוט עודכן.');
          }
        } else if (hoursChanged) {
          showActionSuccess('שעות הסלוט עודכנו.');
        }
      }

      onCloseSlotModal();
      await refreshCurrentWeek();
    } catch (requestError: unknown) {
      showActionError(translateScheduleErrorMessage(requestError, 'שמירת הסלוט נכשלה.'));
    } finally {
      slotSaveLock.current = false;
      setIsSavingModal(false);
    }
  };

  const weekRangeLabel = scheduleWeek
    ? formatWeekRange(scheduleWeek.weekStartDate, scheduleWeek.weekEndDate)
    : '';

  const onExportSchedule = async () => {
    if (!scheduleWeek) {
      return;
    }
    const XLSX = await import('xlsx');
    const rows = sortedSections.flatMap((section) =>
      section.shifts.flatMap((shift) =>
        shift.roles.flatMap((role) =>
          role.slots.map((slot) => ({
            מחלקה: section.sectionName,
            משמרת: shift.shiftName,
            תפקיד: role.roleName,
            תאריך: slot.dateDay,
            התחלה: slot.startHour,
            סיום: slot.endHour,
            עובד: slot.employeeName ?? 'לא שובץ',
          })),
        ),
      ),
    );
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Schedule');
    XLSX.writeFile(workbook, `shift-schedule-${scheduleWeek.weekStartDate}.xlsx`);
    setIsOptionsOpen(false);
  };

  return {
    user,
    canAssignEmployees,
    canManageSchedule,
    isPublishing,
    onChangePublication,
    tipDate,
    setTipDate,
    setWeekStartDate,
    scheduleWeek,
    isLoadingWeek,
    weekError,
    selectedDayOffsets,
    setSelectedDayOffsets,
    selectedShiftNames,
    setSelectedShiftNames,
    selectedRoleNames,
    setSelectedRoleNames,
    isFiltersOpen,
    setIsFiltersOpen,
    isOptionsOpen,
    setIsOptionsOpen,
    nameModal,
    setNameModal,
    slotModal,
    setSlotModal,
    slotCandidatesSearch,
    setSlotCandidatesSearch,
    isLoadingSlotCandidates,
    isSavingModal,
    createSlotEmployees,
    employeeInsight,
    isLoadingEmployeeInsight,
    filtersWrapRef,
    isBusy,
    onOpenEmployeeInsight,
    onCloseEmployeeInsight,
    sortedSections,
    employeeInsightSummaries,
    shiftFilterOptions,
    roleFilterOptions,
    visibleWeekDays,
    activeFilterCount,
    hasActiveFilters,
    visibleSections,
    toggleSetValue,
    onResetFilters,
    onSelectAllFilters,
    filteredSlotCandidates,
    refreshCurrentWeek,
    onCloseSlotModal,
    onDeleteSection,
    onDeleteShift,
    onDeleteRole,
    onDuplicateRole,
    onDeleteSlot,
    onOpenCreateSectionModal,
    onOpenEditSectionModal,
    onOpenCreateShiftModal,
    onOpenEditShiftModal,
    onOpenCreateRoleModal,
    onOpenEditRoleModal,
    onOpenCreateSlotModal,
    onOpenEditSlotModal,
    onNameModalSubmit,
    onSlotModalSubmit,
    weekRangeLabel,
    onExportSchedule,
  };
}
